/* eslint-disable @typescript-eslint/no-unused-vars */
import { useCallback, useRef, useEffect } from "react";
import { useSelector } from "react-redux";
import { AppState } from "@Store/index";
import { trackKeyUsage, isValidTranslationKey } from "helpers/trackUsedKeys";

/**
 * Translation lookup hook.
 *
 * Missing-key Critical alerts are owned solely by compareAndAlert (SSR post-render /
 * CSR post-load). This hook only records usage via trackKeyUsage — it must not call
 * Sentry, or load-gap / warm-cache races dual-fire false Criticals.
 */
const useTranslationHook = () => {
  const { tolgeeTranslationKeys, isLoadingTranslations }: any = useSelector(
    ({ appData }: AppState) => appData,
  );

  const clientTrackedKeys = useRef(new Set<string>());
  const hasLoggedSummary = useRef(false);

  useEffect(() => {
    if (typeof window !== "undefined") {
      clientTrackedKeys.current.clear();
      hasLoggedSummary.current = false;
    }
  }, [typeof window !== "undefined" ? window.location.pathname : ""]);

  useEffect(() => {
    if (typeof window !== "undefined" && !hasLoggedSummary.current) {
      const logSummary = () => {
        if (clientTrackedKeys.current.size > 0) {
          hasLoggedSummary.current = true;
        }
      };

      const timer = setTimeout(logSummary, 3000);
      return () => clearTimeout(timer);
    }
    return undefined;
  }, [tolgeeTranslationKeys]);

  const t = useCallback(
    (key: string, _value?: Record<string, string>, options?: { dynamic?: boolean }) => {
      try {
        const isDynamic = options?.dynamic ?? false;

        trackKeyUsage(key, isDynamic);
        if (typeof window !== "undefined" && key && typeof key === "string") {
          clientTrackedKeys.current.add(key);
        }

        // Return empty string while translations are loading
        if (isLoadingTranslations) {
          return "";
        }

        const langObj = tolgeeTranslationKeys;

        if (!key || typeof key !== "string") {
          return key || "";
        }

        const resolvedValue = langObj ? langObj[key] : undefined;

        // Never render unresolved namespaced keys (e.g. "searchpage.foo.bar") in the UI.
        // Legacy English-as-key strings still fall back to themselves.
        if (resolvedValue === undefined) {
          if (!isDynamic && isValidTranslationKey(key, true)) {
            return "";
          }
          return key;
        }

        const translated = resolvedValue;

        if (typeof translated === "string" && _value) {
          return Object.keys(_value).reduce(
            (str, k) => str.replace(new RegExp(`{${k}}`, "g"), _value[k]),
            translated,
          );
        }

        return translated;
      } catch (error) {
        if (key && isValidTranslationKey(key, true)) {
          return "";
        }
        return key || "";
      }
    },
    [tolgeeTranslationKeys, isLoadingTranslations],
  );

  return { t, isLoadingTranslations };
};

export default useTranslationHook;
